import React, { useState } from 'react';
import { DeliveryScheduleDialog, DeliverySchedule } from './DeliveryScheduleDialog';
import { ScheduledDeliveryDialog, ScheduledDeliveryConfig } from './ScheduledDeliveryDialog';

interface TimePeriod {
  start: string;
  end: string;
}

interface DaySchedule {
  periods: TimePeriod[];
  non_business: boolean;
  is_24_7?: boolean;
}

interface TimetableData {
  weekly: Record<string, DaySchedule>;
  special_days: any[];
}

export interface DeliveryTypesConfig {
  delivery_enabled: number;
  click_and_collect: number;
  delivery_scheduling_enabled: number;
  click_and_collect_scheduling_enabled: number;
  delivery_schedule: DeliverySchedule | null;
  click_and_collect_schedule: DeliverySchedule | null;
  delivery_scheduled_config: ScheduledDeliveryConfig | null;
  click_and_collect_scheduled_config: ScheduledDeliveryConfig | null;
  // Keep old fields for backward compat during transition
  scheduled_delivery_enabled?: number;
  scheduled_delivery_config?: ScheduledDeliveryConfig | null;
}

interface DeliveryTypesSectionProps {
  config: DeliveryTypesConfig;
  onChange: (config: DeliveryTypesConfig) => void;
  workingTimetable: TimetableData | null;
  readOnly?: boolean;
}

const DAY_NAMES: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function summarizeSchedule(schedule: DeliverySchedule | null): string {
  if (!schedule || !schedule.weekly) return 'Full working hours';

  const parts: string[] = [];
  for (const day of DAY_KEYS) {
    const dayCfg = schedule.weekly[day];
    if (dayCfg && dayCfg.active && dayCfg.periods && dayCfg.periods.length > 0) {
      const periods = dayCfg.periods.map(p => `${p.start}–${p.end}`).join(', ');
      parts.push(`${DAY_NAMES[day]}: ${periods}`);
    }
  }
  return parts.length > 0 ? parts.join(' | ') : 'Full working hours';
}

function summarizeScheduledDelivery(config: ScheduledDeliveryConfig | null): string {
  if (!config || !config.slots) return 'Not configured';

  // Sort numerically: object key iteration order puts integer-like keys ("10".."23")
  // before leading-zero keys ("00".."09"), which would otherwise produce wrong ranges.
  const activeHours = Object.entries(config.slots)
    .filter(([, active]) => active)
    .map(([hour]) => parseInt(hour, 10))
    .sort((a, b) => a - b);

  if (activeHours.length === 0) return 'No active slots';
  if (activeHours.length === 24) return 'All slots active';

  const formatHour = (h: number) => `${String(h).padStart(2, '0')}:00`;

  const parts: string[] = [];
  if (activeHours.length <= 4) {
    parts.push(activeHours.map(formatHour).join(', '));
  } else {
    // Each slot key is the start of an hour-long slot, so the window ends one hour after the last slot.
    const firstLabel = formatHour(activeHours[0]);
    const lastLabel = formatHour((activeHours[activeHours.length - 1] + 1) % 24);
    parts.push(`${activeHours.length} slots active (${firstLabel}–${lastLabel})`);
  }

  if (config.max_orders_per_slot) {
    parts.push(`max ${config.max_orders_per_slot}/slot`);
  }
  if (config.min_preorder_hours) {
    parts.push(`min ${config.min_preorder_hours}h preorder`);
  }

  return parts.join(' · ');
}

export const DeliveryTypesSection: React.FC<DeliveryTypesSectionProps> = ({
  config,
  onChange,
  workingTimetable,
  readOnly,
}) => {
  const [showDeliveryScheduleFor, setShowDeliveryScheduleFor] = useState<'delivery' | 'click_and_collect' | null>(null);
  const [showScheduledDeliveryFor, setShowScheduledDeliveryFor] = useState<'delivery' | 'click_and_collect' | null>(null);

  const handleToggle = (field: keyof DeliveryTypesConfig, value: number) => {
    onChange({ ...config, [field]: value });
  };

  type AsapKey = 'delivery_enabled' | 'click_and_collect';
  type ScheduledKey = 'delivery_scheduling_enabled' | 'click_and_collect_scheduling_enabled';

  const renderAsapCard = (
    label: string,
    enabledKey: AsapKey,
    scheduleSummary: string,
    scheduleDialogTarget: 'delivery' | 'click_and_collect',
  ) => (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!config[enabledKey]}
            onChange={(e) => handleToggle(enabledKey, e.target.checked ? 1 : 0)}
            disabled={readOnly}
            className="h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
          />
          <span className="font-medium text-gray-900">{label}</span>
        </label>
        {!!config[enabledKey] && (
          <button
            type="button"
            onClick={() => setShowDeliveryScheduleFor(scheduleDialogTarget)}
            disabled={readOnly}
            className="text-sm text-alps-primary hover:text-alps-primary/80 font-medium disabled:opacity-50"
          >
            Set active schedule
          </button>
        )}
      </div>
      {!!config[enabledKey] && (
        <p className="mt-1 text-xs text-gray-500 ml-7">{scheduleSummary}</p>
      )}
    </div>
  );

  const renderScheduledCard = (
    label: string,
    enabledKey: ScheduledKey,
    summary: string,
    slotsDialogTarget: 'delivery' | 'click_and_collect',
  ) => (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!config[enabledKey]}
            onChange={(e) => handleToggle(enabledKey, e.target.checked ? 1 : 0)}
            disabled={readOnly}
            className="h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
          />
          <span className="font-medium text-gray-900">{label}</span>
        </label>
        {!!config[enabledKey] && (
          <button
            type="button"
            onClick={() => setShowScheduledDeliveryFor(slotsDialogTarget)}
            disabled={readOnly}
            className="text-sm text-alps-primary hover:text-alps-primary/80 font-medium disabled:opacity-50"
          >
            Set time slots
          </button>
        )}
      </div>
      {!!config[enabledKey] && (
        <p className="mt-1 text-xs text-gray-500 ml-7">{summary}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {renderAsapCard(
        'Delivery',
        'delivery_enabled',
        summarizeSchedule(config.delivery_schedule),
        'delivery',
      )}
      {renderScheduledCard(
        'Delivery — Scheduled',
        'delivery_scheduling_enabled',
        summarizeScheduledDelivery(config.delivery_scheduled_config),
        'delivery',
      )}
      {renderAsapCard(
        'Click & Collect',
        'click_and_collect',
        summarizeSchedule(config.click_and_collect_schedule),
        'click_and_collect',
      )}
      {renderScheduledCard(
        'Click & Collect — Scheduled',
        'click_and_collect_scheduling_enabled',
        summarizeScheduledDelivery(config.click_and_collect_scheduled_config),
        'click_and_collect',
      )}

      {/* Delivery / C&C Schedule Dialog */}
      {showDeliveryScheduleFor && (
        <DeliveryScheduleDialog
          open={true}
          title={showDeliveryScheduleFor === 'delivery' ? 'Delivery Schedule' : 'Click & Collect Schedule'}
          schedule={
            showDeliveryScheduleFor === 'delivery'
              ? config.delivery_schedule
              : config.click_and_collect_schedule
          }
          workingTimetable={workingTimetable}
          onSave={(schedule) => {
            const field = showDeliveryScheduleFor === 'delivery' ? 'delivery_schedule' : 'click_and_collect_schedule';
            onChange({ ...config, [field]: schedule });
            setShowDeliveryScheduleFor(null);
          }}
          onCancel={() => setShowDeliveryScheduleFor(null)}
        />
      )}

      {/* Scheduled Delivery Dialog (per type) */}
      {showScheduledDeliveryFor && (
        <ScheduledDeliveryDialog
          open={true}
          config={
            showScheduledDeliveryFor === 'delivery'
              ? config.delivery_scheduled_config
              : config.click_and_collect_scheduled_config
          }
          workingTimetable={workingTimetable}
          onSave={(sdConfig) => {
            const field = showScheduledDeliveryFor === 'delivery'
              ? 'delivery_scheduled_config'
              : 'click_and_collect_scheduled_config';
            onChange({ ...config, [field]: sdConfig });
            setShowScheduledDeliveryFor(null);
          }}
          onCancel={() => setShowScheduledDeliveryFor(null)}
        />
      )}
    </div>
  );
};
